/**
 * The toast stack, top right, with the countdown drawn.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TIMER IS VISIBLE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A message that disappears on its own has to say when. Without a countdown the reader
 * cannot tell a notice that is about to go from one that is staying, so they either rush or
 * they lose it — and a sign-in error they lost is one they cannot ask for again. The bar
 * across the bottom of each toast IS the remaining time.
 *
 * It pauses on hover and on focus, for the same reason: something vanishing mid-sentence,
 * or while a keyboard user is tabbing to its dismiss button, is worse than not auto-
 * dismissing at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ACCESSIBILITY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `role="status"` with `aria-live="polite"` for the container, so arrivals are announced
 * without cutting off whatever is being read. Errors additionally carry `role="alert"` on
 * the toast itself, because a failed sign-in is worth interrupting for — that is the one
 * case where the reader's next action depends on hearing it.
 *
 * The animation is skipped under `prefers-reduced-motion`; the countdown bar is not, since
 * it is information rather than decoration.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { TOAST_LIFE, useToasts, type Toast as ToastItem, type ToastTone } from '@/app/toasts';

const TONE: Record<ToastTone, { rgb: string; ink: string; icon: 'ic-alert' | 'ic-check' | 'ic-info' }> = {
  error: { rgb: 'var(--danger-rgb)', ink: 'var(--danger-ink)', icon: 'ic-alert' },
  success: { rgb: 'var(--success-rgb)', ink: 'var(--success-ink)', icon: 'ic-check' },
  info: { rgb: 'var(--info-rgb)', ink: 'var(--info-ink)', icon: 'ic-info' },
};

/**
 * `belowHeader` for the console, where the top-right corner is the header's own controls — the
 * bell among them, which a notification's pop-up must not sit on top of. The sign-in screen has
 * no header and keeps the corner.
 */
export function Toasts({ belowHeader = false }: { belowHeader?: boolean } = {}): ReactNode {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  if (toasts.length === 0) return null;

  return (
    <div
      className={`pointer-events-none fixed right-4 z-[100] flex flex-col gap-2 ${belowHeader ? 'top-[64px]' : 'top-4'}`}
      style={{ width: 380, maxWidth: 'calc(100vw / var(--app-zoom, 1) - 32px)' }}
      role="status"
      aria-live="polite"
      aria-label="Notifications"
    >
      {/* `dismiss` is passed whole, not wrapped in a closure. A fresh function each render
          lands in the child's effect dependencies and re-arms every visible toast's timer on
          every store update — which on a second failed attempt means the first toast never
          leaves. The alarm stack had exactly this bug. */}
      {toasts.map((t) => (
        <Toast key={t.key} item={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function Toast({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (key: string) => void;
}): ReactNode {
  const [paused, setPaused] = useState(false);
  const life = TOAST_LIFE[item.tone];
  const tone = TONE[item.tone];

  /*
   * The remaining fraction, driven by rAF rather than a CSS transition.
   *
   * A CSS animation cannot be paused and resumed from its current position without either
   * reading the computed style back or restarting it, and a countdown that restarts on
   * hover is lying about how long is left.
   */
  const [remaining, setRemaining] = useState(1);
  const startedAt = useRef<number>(0);
  const elapsed = useRef(0);

  useEffect(() => {
    if (paused) return;
    let frame = 0;
    startedAt.current = performance.now();

    const tick = (now: number): void => {
      const total = elapsed.current + (now - startedAt.current);
      const left = Math.max(0, 1 - total / life);
      setRemaining(left);
      if (left <= 0) {
        onDismiss(item.key);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      /* Bank the time already spent, so resuming continues rather than restarts. */
      elapsed.current += performance.now() - startedAt.current;
    };
  }, [paused, life, item.key, onDismiss]);

  return (
    <div
      className="lm-overlay lm-slidein pointer-events-auto overflow-hidden"
      style={{
        borderLeft: `3px solid rgb(${tone.rgb})`,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderLeftWidth: 3,
        borderLeftColor: `rgb(${tone.rgb})`,
        borderRadius: 'var(--radius)',
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      {...(item.tone === 'error' ? { role: 'alert' } : {})}
    >
      <div className="flex items-start gap-[10px] p-[12px_13px]">
        <span style={{ color: tone.ink, flexShrink: 0, marginTop: 1 }}>
          <Icon name={tone.icon} className="h-[15px] w-[15px]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] leading-[19px]" style={{ color: 'var(--fg)' }}>
            {item.text}
          </p>
          {item.hint != null && (
            <p className="m-0 mt-[3px] text-[11.5px] leading-[17px]" style={{ color: 'var(--muted-fg)' }}>
              {item.hint}
            </p>
          )}
          {item.action !== undefined && (
            <button
              type="button"
              onClick={() => {
                item.action?.run();
                onDismiss(item.key);
              }}
              className="lm-press mt-[6px] rounded-[var(--radius-sm)] border border-[var(--border)] px-[9px] py-[3px] text-[12px] font-semibold text-[var(--fg)]"
            >
              {item.action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(item.key)}
          aria-label="Dismiss"
          className="lm-press shrink-0 rounded-[4px] p-[2px]"
          style={{ color: 'var(--muted-fg)', lineHeight: 0 }}
        >
          <Icon name="ic-x" className="h-[13px] w-[13px]" />
        </button>
      </div>

      {/* THE COUNTDOWN. Two pixels, full width, shrinking left — information, not chrome, so
          it is not suppressed under prefers-reduced-motion. `aria-hidden` because the text
          above already carries the meaning and a shrinking bar announces nothing useful. */}
      <div aria-hidden style={{ height: 2, background: `rgb(${tone.rgb} / 15%)` }}>
        <div
          style={{
            height: '100%',
            width: `${remaining * 100}%`,
            background: `rgb(${tone.rgb} / ${paused ? 45 : 80}%)`,
          }}
        />
      </div>
    </div>
  );
}
